import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { PortalInviteTemplateRepository } from '~/repositories/settings/portalInviteTemplateRepository';
import type { Accessor } from 'solid-js';
import type {
  PortalInviteTemplateDto,
  CreatePortalInviteTemplateDto,
  UpdatePortalInviteTemplateDto,
  PortalInviteMergeFieldDto,
} from '~/swagger/settings/Api';

interface PortalInviteTemplateContextValue {
  templates: Accessor<PortalInviteTemplateDto[] | undefined>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  selectedTemplate: Accessor<PortalInviteTemplateDto | undefined>;
  selectTemplate: (template: PortalInviteTemplateDto) => void;
  loadTemplates: () => Promise<void>;
  updateTemplate: (id: string, payload: UpdatePortalInviteTemplateDto) => Promise<void>;
  resetTemplate: (id: string) => Promise<void>;
  addCustomTemplate: (payload: CreatePortalInviteTemplateDto) => Promise<PortalInviteTemplateDto | undefined>;
  deleteTemplate: (id: string) => Promise<void>;
  mergeFields: Accessor<PortalInviteMergeFieldDto[] | undefined>;
  loadMergeFields: () => Promise<void>;
}

const repo = new PortalInviteTemplateRepository();

export const [PortalInviteTemplateProvider, usePortalInviteTemplates] = createMagicDoorContext<PortalInviteTemplateContextValue>(
  'PortalInviteTemplates',
  () => {
    const [templates, setTemplates] = createSignal<PortalInviteTemplateDto[] | undefined>(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);
    const [selectedTemplate, setSelectedTemplate] = createSignal<PortalInviteTemplateDto | undefined>(undefined);
    const [mergeFields, setMergeFields] = createSignal<PortalInviteMergeFieldDto[] | undefined>(undefined);

    const loadTemplates = async () => {
      setIsLoading(true);
      setError(undefined);
      try {
        const result = await repo.getTemplates();
        setTemplates(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const loadMergeFields = async () => {
      try {
        const result = await repo.getMergeFields();
        setMergeFields(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    };

    const selectTemplate = (template: PortalInviteTemplateDto) => {
      setSelectedTemplate(template);
    };

    const updateTemplate = async (id: string, payload: UpdatePortalInviteTemplateDto) => {
      setError(undefined);
      try {
        const updated = await repo.updateTemplate(id, payload);
        setTemplates((prev) => prev?.map((t) => (t.id === id ? updated : t)));
        setSelectedTemplate(updated);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    };

    const resetTemplate = async (id: string) => {
      setError(undefined);
      try {
        const reset = await repo.resetTemplate(id);
        setTemplates((prev) => prev?.map((t) => (t.id === id ? reset : t)));
        setSelectedTemplate(reset);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    };

    const addCustomTemplate = async (payload: CreatePortalInviteTemplateDto) => {
      setError(undefined);
      try {
        const created = await repo.createTemplate(payload);
        setTemplates((prev) => (prev ? [...prev, created] : [created]));
        setSelectedTemplate(created);
        return created;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    };

    const deleteTemplate = async (id: string) => {
      setError(undefined);
      try {
        await repo.deleteTemplate(id);
        setTemplates((prev) => prev?.filter((t) => t.id !== id));
        setSelectedTemplate(undefined);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    };

    return {
      templates,
      isLoading,
      error,
      selectedTemplate,
      selectTemplate,
      loadTemplates,
      updateTemplate,
      resetTemplate,
      addCustomTemplate,
      deleteTemplate,
      mergeFields,
      loadMergeFields,
    };
  }
);
